/**
 * Customize modal: pick a player skin (preset palette) + cosmetic
 * overlays (hat / gloves / boots). Selections are persisted to
 * localStorage via PlayerSkin and an `onApply` callback fires every
 * time the user changes anything so the live ragdoll re-skins
 * immediately. No close-confirm: every change is auto-saved.
 *
 * DOM-only (uses createModalShell). Preview is a small <canvas> drawn
 * with 2D primitives that mirror the Pixi Ragdoll palette + cosmetics
 * — close enough that a player can tell what their character will look
 * like in-game without the cost of mounting a Pixi instance in the DOM.
 */

import { createModalShell } from './modalUtils.js';
import {
  SKIN_PRESETS, HAT_OPTIONS, GLOVES_OPTIONS, BOOTS_OPTIONS,
  HAT_COLORS, GLOVES_COLORS, BOOTS_COLORS,
  CUSTOM_PRESET_ID,
  getActiveSkin, getCustomColors,
  setActiveSkinId, setCosmetic, setCustomColor,
  type ActiveSkin, type HatId, type GlovesId, type BootsId, type SkinPreset, type SkinColors,
} from '../../../gameplay/PlayerSkin.js';

export interface CustomizeController {
  close(): void;
}

export interface CustomizeOptions {
  /** Fires whenever the active skin changes (auto-saved). The host should
   *  call ragdoll.applySkin() so the live character updates. */
  onApply: (skin: ActiveSkin) => void;
  onClose?: () => void;
}

export function openCustomizeModal(opts: CustomizeOptions): CustomizeController {
  const shell = createModalShell({
    width: 'min(560px, 96vw)',
    backdrop: 'radial-gradient(circle at 50% 30%, rgba(80,40,140,0.35), rgba(0,0,0,0.75))',
    panelBackground: 'linear-gradient(180deg, #1a0e30 0%, #0a0518 100%)',
    panelBorder: '1px solid #6a4aaa',
    ...(opts.onClose ? { onClose: opts.onClose } : {}),
  });
  Object.assign(shell.panel.style, {
    padding: '18px', gap: '12px', borderRadius: '14px',
    boxShadow: '0 8px 40px rgba(120,80,255,0.25)',
    maxHeight: '92vh', overflowY: 'auto',
  } as Partial<CSSStyleDeclaration>);

  // ---- Header --------------------------------------------------------------
  const header = document.createElement('div');
  Object.assign(header.style, { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' } as Partial<CSSStyleDeclaration>);
  const heading = document.createElement('div');
  heading.textContent = 'CUSTOMIZE';
  Object.assign(heading.style, { fontWeight: '800', fontSize: '20px', letterSpacing: '0.12em' } as Partial<CSSStyleDeclaration>);
  const sub = document.createElement('div');
  sub.textContent = 'auto-saved';
  Object.assign(sub.style, { fontSize: '11px', opacity: '0.55' } as Partial<CSSStyleDeclaration>);
  header.append(heading, sub);
  shell.panel.appendChild(header);

  // ---- Preview -------------------------------------------------------------
  const previewWrap = document.createElement('div');
  Object.assign(previewWrap.style, {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    background: 'radial-gradient(circle at 50% 60%, rgba(255,255,255,0.05), rgba(0,0,0,0))',
    borderRadius: '10px', padding: '8px',
  } as Partial<CSSStyleDeclaration>);
  const canvas = document.createElement('canvas');
  canvas.width = 160;
  canvas.height = 200;
  Object.assign(canvas.style, { width: '160px', height: '200px', imageRendering: 'crisp-edges' } as Partial<CSSStyleDeclaration>);
  previewWrap.appendChild(canvas);
  shell.panel.appendChild(previewWrap);

  // ---- Active state --------------------------------------------------------
  let active = getActiveSkin();

  const apply = (): void => {
    redrawPreview();
    redrawSelections();
    opts.onApply(active);
  };

  // ---- Skin presets grid ---------------------------------------------------
  const skinSection = section('Skin');
  shell.panel.appendChild(skinSection.wrap);
  const skinGrid = document.createElement('div');
  Object.assign(skinGrid.style, {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))',
    gap: '8px',
  } as Partial<CSSStyleDeclaration>);
  skinSection.body.appendChild(skinGrid);

  const skinCards: Array<{ id: string; el: HTMLButtonElement }> = [];
  for (const preset of SKIN_PRESETS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    Object.assign(btn.style, {
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
      padding: '8px 4px',
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '8px', cursor: 'pointer', font: 'inherit', color: '#eee',
    } as Partial<CSSStyleDeclaration>);
    btn.appendChild(swatch(preset));
    const name = document.createElement('div');
    name.textContent = preset.name;
    Object.assign(name.style, { fontSize: '11px', opacity: '0.85' } as Partial<CSSStyleDeclaration>);
    btn.appendChild(name);
    btn.addEventListener('click', () => {
      setActiveSkinId(preset.id);
      active = getActiveSkin();
      apply();
    });
    skinGrid.appendChild(btn);
    skinCards.push({ id: preset.id, el: btn });
  }

  // ---- Custom color editor (only visible when 'Custom' is active) --------
  const customWrap = document.createElement('div');
  Object.assign(customWrap.style, {
    display: 'none', flexDirection: 'column', gap: '6px',
    padding: '10px', borderRadius: '8px',
    background: 'rgba(255,170,85,0.06)',
    border: '1px solid rgba(255,170,85,0.25)',
  } as Partial<CSSStyleDeclaration>);
  const customLabel = document.createElement('div');
  customLabel.textContent = 'CUSTOM COLORS';
  Object.assign(customLabel.style, { fontSize: '11px', opacity: '0.7', letterSpacing: '0.12em', fontWeight: '700' } as Partial<CSSStyleDeclaration>);
  customWrap.appendChild(customLabel);
  const customRow = document.createElement('div');
  Object.assign(customRow.style, {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px',
  } as Partial<CSSStyleDeclaration>);
  customWrap.appendChild(customRow);

  const colorInputs: Record<keyof SkinColors, HTMLInputElement> = {} as Record<keyof SkinColors, HTMLInputElement>;
  const slots: Array<{ key: keyof SkinColors; name: string }> = [
    { key: 'skin',   name: 'Head' },
    { key: 'torso',  name: 'Torso' },
    { key: 'limb',   name: 'Limbs' },
    { key: 'stroke', name: 'Outline' },
  ];
  for (const s of slots) {
    const lbl = document.createElement('label');
    Object.assign(lbl.style, {
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '4px 6px', borderRadius: '6px',
      background: 'rgba(255,255,255,0.04)',
      cursor: 'pointer', fontSize: '12px',
    } as Partial<CSSStyleDeclaration>);
    const input = document.createElement('input');
    input.type = 'color';
    Object.assign(input.style, {
      width: '32px', height: '24px', border: 'none', background: 'transparent',
      padding: '0', cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);
    input.addEventListener('input', () => {
      const hex = input.value.startsWith('#') ? input.value.slice(1) : input.value;
      const n = parseInt(hex, 16);
      if (!Number.isFinite(n)) return;
      // Switch to custom preset on first edit so the change is visible
      // (and persists when other presets are picked then re-Custom).
      if (active.preset.id !== CUSTOM_PRESET_ID) setActiveSkinId(CUSTOM_PRESET_ID);
      setCustomColor(s.key, n);
      active = getActiveSkin();
      apply();
    });
    const span = document.createElement('span');
    span.textContent = s.name;
    lbl.append(input, span);
    customRow.appendChild(lbl);
    colorInputs[s.key] = input;
  }
  shell.panel.appendChild(customWrap);

  // ---- Cosmetic slot rows --------------------------------------------------
  const hatRow = optionRow<HatId>('Hat', HAT_OPTIONS, () => active.cosmetics.hat, (id) => {
    setCosmetic('hat', id);
    active = getActiveSkin();
    apply();
  });
  const glovesRow = optionRow<GlovesId>('Gloves', GLOVES_OPTIONS, () => active.cosmetics.gloves, (id) => {
    setCosmetic('gloves', id);
    active = getActiveSkin();
    apply();
  });
  const bootsRow = optionRow<BootsId>('Boots', BOOTS_OPTIONS, () => active.cosmetics.boots, (id) => {
    setCosmetic('boots', id);
    active = getActiveSkin();
    apply();
  });
  shell.panel.append(hatRow.wrap, glovesRow.wrap, bootsRow.wrap);

  // ---- Footer (Done) -------------------------------------------------------
  const footer = document.createElement('div');
  Object.assign(footer.style, { display: 'flex', justifyContent: 'flex-end', marginTop: '4px' } as Partial<CSSStyleDeclaration>);
  const doneBtn = document.createElement('button');
  doneBtn.type = 'button';
  doneBtn.textContent = 'Done';
  Object.assign(doneBtn.style, {
    padding: '8px 16px',
    background: 'linear-gradient(180deg, #5a3aaa 0%, #3a1a7a 100%)',
    color: '#fff', border: '1px solid #aa77dd',
    borderRadius: '8px', cursor: 'pointer', font: 'inherit', fontWeight: '600',
  } as Partial<CSSStyleDeclaration>);
  doneBtn.addEventListener('click', () => shell.close());
  footer.appendChild(doneBtn);
  shell.panel.appendChild(footer);

  const redrawSelections = (): void => {
    for (const card of skinCards) {
      const sel = card.id === active.preset.id;
      card.el.style.borderColor = sel ? '#ffaa55' : 'rgba(255,255,255,0.1)';
      card.el.style.background = sel ? 'rgba(255,170,85,0.18)' : 'rgba(255,255,255,0.04)';
    }
    // Custom panel: visible only when 'Custom' is the active preset.
    const isCustom = active.preset.id === CUSTOM_PRESET_ID;
    customWrap.style.display = isCustom ? 'flex' : 'none';
    if (isCustom) {
      const c = getCustomColors();
      colorInputs.skin.value   = '#' + c.skin.toString(16).padStart(6, '0');
      colorInputs.torso.value  = '#' + c.torso.toString(16).padStart(6, '0');
      colorInputs.limb.value   = '#' + c.limb.toString(16).padStart(6, '0');
      colorInputs.stroke.value = '#' + c.stroke.toString(16).padStart(6, '0');
    }
    hatRow.refresh();
    glovesRow.refresh();
    bootsRow.refresh();
  };

  const redrawPreview = (): void => {
    drawPreview(canvas, active);
  };

  redrawPreview();
  redrawSelections();

  document.body.appendChild(shell.overlay);

  return { close: shell.close };
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function section(title: string): { wrap: HTMLDivElement; body: HTMLDivElement } {
  const wrap = document.createElement('div');
  Object.assign(wrap.style, { display: 'flex', flexDirection: 'column', gap: '6px' } as Partial<CSSStyleDeclaration>);
  const t = document.createElement('div');
  t.textContent = title.toUpperCase();
  Object.assign(t.style, { fontSize: '11px', opacity: '0.6', letterSpacing: '0.12em', fontWeight: '700' } as Partial<CSSStyleDeclaration>);
  const body = document.createElement('div');
  wrap.append(t, body);
  return { wrap, body };
}

function swatch(preset: SkinPreset): HTMLDivElement {
  const wrap = document.createElement('div');
  Object.assign(wrap.style, {
    display: 'grid', gridTemplateColumns: 'repeat(2, 18px)',
    gridTemplateRows: 'repeat(2, 18px)', gap: '2px',
    padding: '2px', borderRadius: '4px',
    background: '#000',
  } as Partial<CSSStyleDeclaration>);
  const dot = (color: number): HTMLDivElement => {
    const d = document.createElement('div');
    d.style.background = '#' + color.toString(16).padStart(6, '0');
    d.style.borderRadius = '3px';
    return d;
  };
  wrap.append(
    dot(preset.colors.skin),
    dot(preset.colors.torso),
    dot(preset.colors.limb),
    dot(preset.colors.stroke),
  );
  return wrap;
}

function optionRow<T extends string>(
  label: string,
  options: ReadonlyArray<{ id: T | null; name: string }>,
  getCurrent: () => T | null,
  onPick: (id: T | null) => void,
): { wrap: HTMLDivElement; refresh: () => void } {
  const s = section(label);
  const row = document.createElement('div');
  Object.assign(row.style, { display: 'flex', flexWrap: 'wrap', gap: '6px' } as Partial<CSSStyleDeclaration>);
  s.body.appendChild(row);
  const buttons: Array<{ id: T | null; el: HTMLButtonElement }> = [];
  for (const opt of options) {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = opt.name;
    Object.assign(b.style, {
      padding: '6px 10px', borderRadius: '6px',
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.1)',
      color: '#eee', cursor: 'pointer', font: 'inherit', fontSize: '12px',
    } as Partial<CSSStyleDeclaration>);
    b.addEventListener('click', () => onPick(opt.id));
    row.appendChild(b);
    buttons.push({ id: opt.id, el: b });
  }
  const refresh = (): void => {
    const cur = getCurrent();
    for (const btn of buttons) {
      const sel = btn.id === cur;
      btn.el.style.borderColor = sel ? '#ffaa55' : 'rgba(255,255,255,0.1)';
      btn.el.style.background = sel ? 'rgba(255,170,85,0.18)' : 'rgba(255,255,255,0.04)';
    }
  };
  return { wrap: s.wrap, refresh };
}

// ----------------------------------------------------------------------------
// Preview rendering (2D canvas, mirrors the Pixi Ragdoll palette + cosmetics)
// ----------------------------------------------------------------------------

function drawPreview(canvas: HTMLCanvasElement, skin: ActiveSkin): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width, h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  // Subtle ground shadow.
  ctx.fillStyle = 'rgba(0,0,0,0.25)';
  ctx.beginPath();
  ctx.ellipse(w / 2, h - 18, 32, 6, 0, 0, Math.PI * 2);
  ctx.fill();

  // Center the figure. Coords mirror the Ragdoll layout (3x scale).
  const cx = w / 2;
  const cy = h / 2 - 6;
  const S = 3;
  const colors = skin.preset.colors;
  const ink = colors.stroke;
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#' + ink.toString(16).padStart(6, '0');

  const rrect = (x: number, y: number, rw: number, rh: number, fill: number): void => {
    const r = Math.min(rw, rh) / 2;
    ctx.fillStyle = '#' + fill.toString(16).padStart(6, '0');
    ctx.beginPath();
    roundRectPath(ctx, x, y, rw, rh, r);
    ctx.fill();
    ctx.stroke();
  };

  // Legs (drawn first so torso overlaps).
  rrect(cx - 5 * S - 3.5, cy + 22 * S - 9 * S, 7, 18 * S, colors.limb); // upperLegL (collapsed vertical)
  // Actually, draw rectangles centered at the part body positions:
  // To stay simple, treat the figure as a top-down stack of body parts.
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = 'rgba(0,0,0,0.25)';
  ctx.beginPath();
  ctx.ellipse(w / 2, h - 14, 36, 6, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.lineWidth = 2;
  ctx.strokeStyle = '#' + ink.toString(16).padStart(6, '0');

  // Position offsets from the Ragdoll constructor (relative to torso):
  //  torso (14x26) at (0, 0); head r=9 at (0, -24)
  //  upperArmL (6x16) at (-10, -6); forearmL at (-10, +10)
  //  upperArmR (6x16) at (+10, -6); forearmR at (+10, +10)
  //  upperLegL (7x18) at (-5, +22); lowerLegL at (-5, +40)
  //  upperLegR (7x18) at (+5, +22); lowerLegR at (+5, +40)
  const at = (lx: number, ly: number): { x: number; y: number } => ({ x: cx + lx * S, y: cy + ly * S });

  const partRect = (lx: number, ly: number, lw: number, lh: number, fill: number): void => {
    const p = at(lx, ly);
    rrect(p.x - (lw * S) / 2, p.y - (lh * S) / 2, lw * S, lh * S, fill);
  };

  // Limbs first.
  partRect(-10, -6, 6, 16, colors.limb); // upperArmL
  partRect(-10, 10, 6, 16, colors.limb); // forearmL
  partRect(10, -6, 6, 16, colors.limb);  // upperArmR
  partRect(10, 10, 6, 16, colors.limb);  // forearmR
  partRect(-5, 22, 7, 18, colors.limb);  // upperLegL
  partRect(-5, 40, 7, 18, colors.limb);  // lowerLegL
  partRect(5, 22, 7, 18, colors.limb);   // upperLegR
  partRect(5, 40, 7, 18, colors.limb);   // lowerLegR

  // Torso + head.
  partRect(0, 0, 14, 26, colors.torso);
  // Head:
  const headP = at(0, -24);
  ctx.fillStyle = '#' + colors.skin.toString(16).padStart(6, '0');
  ctx.beginPath();
  ctx.arc(headP.x, headP.y, 9 * S, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // Idle face: dot eye + small mouth.
  ctx.fillStyle = '#' + ink.toString(16).padStart(6, '0');
  ctx.beginPath();
  ctx.arc(headP.x + 3 * S, headP.y - 1.8 * S, 1.3 * S, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(headP.x + (1.5 - 1.8) * S, headP.y + 3.5 * S);
  ctx.lineTo(headP.x + (1.5 + 1.8) * S, headP.y + 3.5 * S);
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.lineWidth = 2;

  // Cosmetics.
  if (skin.cosmetics.hat) drawHatPreview(ctx, headP.x, headP.y, S, skin.cosmetics.hat, ink);
  if (skin.cosmetics.gloves) {
    const lh = at(-10, 18); const rh = at(10, 18);
    drawGlovePreview(ctx, lh.x, lh.y, S, skin.cosmetics.gloves, ink);
    drawGlovePreview(ctx, rh.x, rh.y, S, skin.cosmetics.gloves, ink);
  }
  if (skin.cosmetics.boots) {
    const lf = at(-5, 49); const rf = at(5, 49);
    drawBootPreview(ctx, lf.x, lf.y, S, skin.cosmetics.boots, ink);
    drawBootPreview(ctx, rf.x, rf.y, S, skin.cosmetics.boots, ink);
  }
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
}

function drawHatPreview(ctx: CanvasRenderingContext2D, x: number, y: number, S: number, id: HatId, ink: number): void {
  const c = HAT_COLORS[id];
  const top = y - 9 * S;
  const fill = '#' + c.fill.toString(16).padStart(6, '0');
  ctx.strokeStyle = '#' + ink.toString(16).padStart(6, '0');
  ctx.lineWidth = 1.4;
  if (id === 'cap') {
    ctx.fillStyle = fill;
    ctx.fillRect(x - 7 * S, top - 1 * S, 14 * S, 2 * S); ctx.strokeRect(x - 7 * S, top - 1 * S, 14 * S, 2 * S);
    roundRectPath(ctx, x - 5 * S, top - 7 * S, 10 * S, 6 * S, 2 * S); ctx.fill(); ctx.stroke();
    ctx.fillRect(x + 2 * S, top - 1 * S, 6 * S, 2 * S); ctx.strokeRect(x + 2 * S, top - 1 * S, 6 * S, 2 * S);
  } else if (id === 'beanie') {
    roundRectPath(ctx, x - 7 * S, top - 6 * S, 14 * S, 7 * S, 3 * S); ctx.fill(); ctx.stroke();
    if (c.band !== undefined) {
      ctx.fillStyle = '#' + c.band.toString(16).padStart(6, '0');
      ctx.fillRect(x - 7 * S, top - 1 * S, 14 * S, 2 * S);
    }
    ctx.beginPath(); ctx.arc(x, top - 7 * S, 1.6 * S, 0, Math.PI * 2);
    ctx.fillStyle = '#' + ((c.band ?? c.fill).toString(16).padStart(6, '0')); ctx.fill(); ctx.stroke();
  } else if (id === 'tophat') {
    ctx.fillStyle = fill;
    ctx.fillRect(x - 9 * S, top - 1 * S, 18 * S, 2 * S); ctx.strokeRect(x - 9 * S, top - 1 * S, 18 * S, 2 * S);
    ctx.fillRect(x - 5 * S, top - 11 * S, 10 * S, 10 * S); ctx.strokeRect(x - 5 * S, top - 11 * S, 10 * S, 10 * S);
    if (c.band !== undefined) {
      ctx.fillStyle = '#' + c.band.toString(16).padStart(6, '0');
      ctx.fillRect(x - 5 * S, top - 4 * S, 10 * S, 1.6 * S);
    }
  } else if (id === 'crown') {
    ctx.fillStyle = fill;
    ctx.fillRect(x - 7 * S, top - 1 * S, 14 * S, 2 * S); ctx.strokeRect(x - 7 * S, top - 1 * S, 14 * S, 2 * S);
    const tri = (a: number, b: number, c2: number, d: number, e: number, f: number): void => {
      ctx.beginPath(); ctx.moveTo(a, b); ctx.lineTo(c2, d); ctx.lineTo(e, f); ctx.closePath(); ctx.fill(); ctx.stroke();
    };
    tri(x - 7 * S, top - 1 * S, x - 5 * S, top - 7 * S, x - 3 * S, top - 1 * S);
    tri(x - 1.5 * S, top - 1 * S, x, top - 8 * S, x + 1.5 * S, top - 1 * S);
    tri(x + 3 * S, top - 1 * S, x + 5 * S, top - 7 * S, x + 7 * S, top - 1 * S);
  } else if (id === 'horns') {
    ctx.fillStyle = fill;
    const tri = (a: number, b: number, c2: number, d: number, e: number, f: number): void => {
      ctx.beginPath(); ctx.moveTo(a, b); ctx.lineTo(c2, d); ctx.lineTo(e, f); ctx.closePath(); ctx.fill(); ctx.stroke();
    };
    tri(x - 5 * S, top, x - 7 * S, top - 6 * S, x - 3 * S, top - 1 * S);
    tri(x + 5 * S, top, x + 7 * S, top - 6 * S, x + 3 * S, top - 1 * S);
  }
  ctx.lineWidth = 2;
}

function drawGlovePreview(ctx: CanvasRenderingContext2D, x: number, y: number, S: number, id: GlovesId, ink: number): void {
  const fill = GLOVES_COLORS[id];
  ctx.fillStyle = '#' + fill.toString(16).padStart(6, '0');
  ctx.strokeStyle = '#' + ink.toString(16).padStart(6, '0');
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.arc(x, y, 4 * S, 0, Math.PI * 2);
  ctx.fill(); ctx.stroke();
  ctx.lineWidth = 2;
}

function drawBootPreview(ctx: CanvasRenderingContext2D, x: number, y: number, S: number, id: BootsId, ink: number): void {
  const fill = BOOTS_COLORS[id];
  ctx.fillStyle = '#' + fill.toString(16).padStart(6, '0');
  ctx.strokeStyle = '#' + ink.toString(16).padStart(6, '0');
  ctx.lineWidth = 1.2;
  roundRectPath(ctx, x - 4 * S, y - 3 * S, 10 * S, 7 * S, 2 * S);
  ctx.fill(); ctx.stroke();
  ctx.fillStyle = '#' + ink.toString(16).padStart(6, '0');
  ctx.fillRect(x - 4 * S, y + 3 * S, 10 * S, 2 * S);
  ctx.lineWidth = 2;
}
